(() => {
  'use strict';

  const FIELD = 'Pakowanie';
  const LEGACY_FIELDS = ['Zestaw', 'ID zestawu', 'Element', 'Slot'];
  const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let pending = Promise.resolve();
  let catalogRequest;
  let catalogReadAt = 0;

  // Use collection membership, not product names, tags or the page visited.
  // Read all pages so newly added ready sets are covered without code changes.
  async function readReadySets() {
    if (catalogRequest && Date.now() - catalogReadAt < 60000) return catalogRequest;
    catalogReadAt = Date.now();
    catalogRequest = (async () => {
      const productIds = new Set(), variantIds = new Set();
      let page = 1, pages = 1;
      do {
        const response = await fetch(`${root()}collections/zestawy-prezentowe-61?view=kk-packing&page=${page}`, {
          headers: { Accept: 'text/html' }, cache: 'no-store',
        });
        if (!response.ok) throw new Error(window.KKText.sets_check_failed);
        const data = await response.json();
        if (data.collection !== 'zestawy-prezentowe-61' || !Array.isArray(data.products) ||
            data.page !== page || !Number.isInteger(data.pages) || data.pages < page) {
          throw new Error(window.KKText.sets_load_failed);
        }
        pages = data.pages;
        for (const product of data.products) {
          productIds.add(String(product.id));
          for (const id of product.variants) variantIds.add(String(id));
        }
        page++;
      } while (page <= pages);
      return { productIds, variantIds };
    })();
    try { return await catalogRequest; }
    catch (error) { catalogRequest = null; throw error; }
  }

  const isReadySet = (item, catalog) => catalog.productIds.has(String(item.product_id)) || catalog.variantIds.has(String(item.variant_id));

  async function prepareFormData(formData) {
    if (formData.get('properties[_kk_configuration_id]')) return;
    const catalog = await readReadySets();
    if (catalog.variantIds.has(String(formData.get('id')))) {
      formData.delete(`properties[${FIELD}]`);
      formData.set('properties[_kk_ready_set]', 'true');
    } else {
      formData.delete('properties[_kk_ready_set]');
      formData.set(`properties[${FIELD}]`, 'luzem');
    }
  }

  function groupsIn(cart) {
    const groups = new Map();
    // Shopify lists the newest added lines first. Legacy configurations have
    // no ordinal, so their first occurrence in reverse order supplies a fallback.
    [...cart.items].reverse().forEach(item => {
      const p = item.properties || {};
      if (!p._kk_configuration_id || groups.has(p._kk_configuration_id)) return;
      groups.set(p._kk_configuration_id, {
        id: p._kk_configuration_id,
        layout: String(p._kk_layout || ''),
        sequence: Number(p._kk_sequence) || 0,
        position: groups.size,
      });
    });
    return [...groups.values()];
  }

  function labelsFor(cart) {
    const groups = groupsIn(cart);
    for (const group of groups) {
      if (!group.layout) throw new Error(window.KKText.set_number_failed);
    }
    // Number configurations across the whole cart, regardless of box layout.
    groups.sort((a, b) => (a.sequence || a.position + 1) - (b.sequence || b.position + 1) || a.position - b.position);
    const labels = new Map();
    groups.forEach((group, index) => labels.set(group.id, `Zestaw ${index + 1}|Do złożenia`));
    return labels;
  }

  function nextSequence(cart, editingId) {
    const groups = groupsIn(cart);
    const existing = groups.find(group => group.id === editingId);
    if (existing) return existing.sequence || existing.position + 1;
    return Math.max(0, ...groups.map(group => group.sequence || group.position + 1)) + 1;
  }

  function desiredProperties(item, labels, catalog) {
    const properties = { ...(item.properties || {}) };
    const configurationId = properties._kk_configuration_id;
    if (configurationId) {
      delete properties._kk_ready_set;
      for (const field of LEGACY_FIELDS) delete properties[field];
      if (properties['Wstążka'] === 'Bez wstążki') delete properties['Wstążka'];
      if (properties.Kartka === 'Bez kartki') delete properties.Kartka;
      properties[FIELD] = labels.get(configurationId);
    } else if (isReadySet(item, catalog)) {
      delete properties[FIELD];
      // Shopify cannot clear properties with an empty object. A private marker
      // also lets old carts drop their incorrect public "luzem" value safely.
      properties._kk_ready_set = 'true';
    } else {
      delete properties._kk_ready_set;
      properties[FIELD] = 'luzem';
    }
    return properties;
  }

  const sorted = object => JSON.stringify(Object.entries(object || {}).sort(([a], [b]) => a.localeCompare(b)));
  function identity(item) {
    const properties = { ...(item.properties || {}) };
    delete properties[FIELD];
    delete properties._kk_ready_set;
    if (properties._kk_configuration_id) {
      for (const field of LEGACY_FIELDS) delete properties[field];
      if (properties['Wstążka'] === 'Bez wstążki') delete properties['Wstążka'];
      if (properties.Kartka === 'Bez kartki') delete properties.Kartka;
    }
    return `${item.variant_id}:${item.selling_plan_allocation?.selling_plan?.id || ''}:${sorted(properties)}`;
  }

  async function readCart() {
    const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(window.KKText.cart_read_retry);
    return response.json();
  }

  async function reconcileNow({ sections = [] } = {}) {
    const [initialCart, catalog] = await Promise.all([readCart(), readReadySets()]);
    let cart = initialCart;
    const labels = labelsFor(cart);
    const changes = cart.items.filter(item => sorted(item.properties) !== sorted(desiredProperties(item, labels, catalog)))
      .map(item => ({ key: item.key, identity: identity(item) }));
    for (const change of changes) {
      const item = cart.items.find(line => line.key === change.key) || cart.items.find(line => identity(line) === change.identity);
      if (!item) continue; // A line may have merged with an identical loose item.
      const properties = desiredProperties(item, labels, catalog);
      if (sorted(properties) === sorted(item.properties)) continue;
      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: item.quantity, properties }),
      });
      const result = await response.json();
      if (!response.ok || !Array.isArray(result.items)) throw new Error(result.description || window.KKText.packing_save_failed);
      cart = result;
    }
    if (sections.length) {
      const url = new URL(root(), location.origin);
      url.searchParams.set('sections', sections.join(','));
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(window.KKText.cart_refresh_failed);
      cart.sections = await response.json();
    }
    return cart;
  }

  function reconcile(options) {
    const task = pending.then(() => reconcileNow(options));
    pending = task.catch(() => {});
    return task;
  }

  window.KKCartPacking = { labelsFor, nextSequence, desiredProperties, readCart, reconcile, prepareFormData };
})();
